import type { CharacterStay } from "./rail-branches";
import type { Character } from "./schema/character";
import type { Place } from "./schema/place";
import type { Scene } from "./schema/scene";

const SELF_SEPARATOR = "@";
const SELF_SUFFIX = "flashback";

/** `harry-potter@flashback` is Harry in a flashback, on stage alongside Harry. */
export function isFlashbackSelf(characterId: string): boolean {
  return characterId.includes(SELF_SEPARATOR);
}

export function flashbackSelfOriginal(characterId: string): string {
  return characterId.split(SELF_SEPARATOR)[0] ?? characterId;
}

function isOnStage(role: string): boolean {
  return role === "present" || role === "pov";
}

/**
 * Book order only. Someone in a flashback at another site, who is also in the scenes
 * either side of it, is on two rails at once, so the flashback gets its own marker in
 * whatever look the scene gives them. At the same site there is only one rail, so
 * there is only one of them. `withoutFlashbackSelves` undoes this for story order.
 */
export function splitFlashbackSelves(
  scenes: readonly Scene[],
  characters: readonly Character[],
  places: readonly Place[] = [],
): { scenes: Scene[]; characters: Character[] } {
  const siteOf = (scene: Scene | undefined): string | undefined => {
    const place = places.find((item) => item.id === scene?.placeId);
    return place?.parentId ?? place?.id ?? scene?.placeId ?? scene?.location.label;
  };
  const ordered = [...scenes].sort((a, b) => a.bookOrder - b.bookOrder);
  const latestOrders = ordered.map((_, index) => Math.max(
    ...ordered.slice(0, index).map((scene) => scene.inUniverseOrder),
    Number.NEGATIVE_INFINITY,
  ));
  const isFlashback = (index: number): boolean => (
    (ordered[index]?.inUniverseOrder ?? 0) < (latestOrders[index] ?? 0)
  );
  const selves = new Map<string, Character>();
  const split = new Map<string, Scene>();
  ordered.forEach((scene, index) => {
    if (!isFlashback(index)) {
      return;
    }
    const before = ordered.slice(0, index).reverse().find(
      (_, back) => !isFlashback(index - back - 1),
    );
    const after = ordered.slice(index + 1).find(
      (_, ahead) => !isFlashback(index + ahead + 1),
    );
    if (siteOf(scene) === siteOf(before)) {
      return;
    }
    const inMainScene = (other: Scene | undefined, id: string): boolean => (
      other?.participants.some((person) => (
        person.characterId === id && isOnStage(person.role)
      )) ?? false
    );
    const renamed = new Map<string, string>();
    const participants = scene.participants.map((person) => {
        const original = characters.find((item) => item.id === person.characterId);
        if (
          original === undefined || !isOnStage(person.role)
          || !inMainScene(before, person.characterId)
          || !inMainScene(after, person.characterId)
        ) {
          return person;
        }
        const id = `${person.characterId}${SELF_SEPARATOR}${SELF_SUFFIX}`;
        selves.set(id, { ...original, id, flashbackOf: original.id });
        renamed.set(person.characterId, id);
        return { ...person, characterId: id };
    });
    const self = (id: string): string => renamed.get(id) ?? id;
    split.set(scene.id, {
      ...scene,
      participants,
      // Whoever acquires a morph in a flashback is the past self there.
      ...(scene.acquires === undefined ? {} : {
        acquires: scene.acquires.map((item) => ({
          ...item,
          characterId: self(item.characterId),
          fromCharacterId: self(item.fromCharacterId),
        })),
      }),
    });
  });
  return {
    scenes: scenes.map((scene) => (selves.size > 0 ? split.get(scene.id) ?? scene : scene)),
    characters: [...characters, ...selves.values()],
  };
}

/** Story order has no second self: in the flashback's own time, they are just them. */
export function withoutFlashbackSelves(scenes: readonly Scene[]): Scene[] {
  return scenes.map((scene) => ({
    ...scene,
    participants: scene.participants.map((person) => ({
      ...person,
      characterId: flashbackSelfOriginal(person.characterId),
    })),
  }));
}

/**
 * While a flashback self is out, the main scene's cast waits where they were. Back-to-back
 * flashbacks count as one wait, and the story may resume at a different place.
 */
export function holdCastThroughFlashbackSelves(
  stays: readonly CharacterStay[],
): CharacterStay[] {
  let held = [...stays];
  for (const { startMs, endMs } of flashbackSelfRuns(stays)) {
    for (const before of held.filter((stay) => stay.endMs === startMs)) {
      if (isFlashbackSelf(before.characterId)) {
        continue;
      }
      const after = held.find((stay) => (
        stay.characterId === before.characterId && stay.startMs === endMs
      ));
      if (after === undefined) {
        continue;
      }
      const samePlace = after.rootPlaceId === before.rootPlaceId
        && after.placeId === before.placeId
        && after.appearanceId === before.appearanceId;
      held = held
        .filter((stay) => stay !== before && (!samePlace || stay !== after))
        .concat({ ...before, endMs: samePlace ? after.endMs : endMs });
    }
  }
  return held;
}

function flashbackSelfRuns(
  stays: readonly CharacterStay[],
): { startMs: number; endMs: number }[] {
  const runs: { startMs: number; endMs: number }[] = [];
  const selves = stays
    .filter((stay) => isFlashbackSelf(stay.characterId))
    .sort((a, b) => a.startMs - b.startMs);
  for (const { startMs, endMs } of selves) {
    const last = runs[runs.length - 1];
    if (last !== undefined && startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, endMs);
    } else {
      runs.push({ startMs, endMs });
    }
  }
  return runs;
}

/** A flashback self slides out from, and back into, the person they are. */
export function flashbackSelfHost(
  stays: readonly CharacterStay[],
  characterId: string,
  atMs: number,
): string | null {
  if (!isFlashbackSelf(characterId)) {
    return null;
  }
  const original = flashbackSelfOriginal(characterId);
  return stays.some((stay) => (
    stay.characterId === original && stay.startMs <= atMs && stay.endMs >= atMs
  )) ? original : null;
}
